import React, { createContext, useContext, useMemo, useReducer } from "react";
import Firestore from "../handlers/firestore";

const { readProducts } = Firestore;

export const Context = createContext();

const initialState = {
  items: [],
  placeholders: [],
  count: 0,
  inputs: { title: null, file: null, path: null },
  category: null,
  subcategory: null,
  gender: null,
  description: null,
  price: null,
  size: null,
  quantity: null,
  color: null,
  isCollapsed: false,
  likes: {},
};

const handleOnChange = (state, e) => {
  if (e && e.target) {
    if (e.target.name === "file") {
      return {
        ...state.inputs,
        file: e.target.files[0],
        path: URL.createObjectURL(e.target.files[0]),
      };
    } else {
      return { ...state.inputs, [e.target.name]: e.target.value };
    }
  }
  return state.inputs;
};

const updateStatus = (quantity) => {
  return quantity === 0 ? "Sold Out" : "";
};

function reducer(state, action) {
  switch (action.type) {
    case "setItem":
      const newItem = { ...state.inputs, likes: 0 };
      return {
        ...state,
        items: [newItem, ...state.items],
        placeholders: [newItem, ...state.placeholders],
        count: state.count + 1,
        inputs: { title: null, file: null, path: null, quantity: null },
        category: null,
        subcategory: null,
        gender: null,
        description: null,
        price: null,
        size: null,
        color: null,
        isCollapsed: false,
      };
    case "resetInputs":
      return {
        ...state,
        inputs: { title: null, file: null, path: null, quantity: null },
        category: null,
        subcategory: null,
        gender: null,
        description: null,
        price: null,
        size: null,
        color: null,
      };
    case "likeProduct":
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, likes: item.likes + 1 }
            : item
        ),
        likes: {
          ...state.likes,
          [action.payload.id]: (state.likes[action.payload.id] || 0) + 1,
        },
      };
    case "unlikeProduct":
      const updatedLikes = { ...state.likes };
      delete updatedLikes[action.payload.id];
      return {
        ...state,
        likes: updatedLikes,
      };
    case "filterItems":
      return {
        ...state,
        items: action.payload.results,
      };
    case "setItems":
      return {
        ...state,
        items: action.payload.items,
        placeholders: action.payload.items,
      };
    case "setInputs":
      const updatedInputs = handleOnChange(state, action.payload.value);
      const updatedStatus = updateStatus(updatedInputs.quantity);
      return {
        ...state,
        inputs: updatedInputs,
        status: updatedStatus,
      };
    case "updateInputs":
      const { value } = action.payload;
      return {
        ...state,
        inputs: {
          title: value.title || "",
          file: value.file,
          path: value.path,
          category: value.category,
          subcategory: value.subcategory,
          gender: value.gender,
          description: value.description,
          price: value.price,
          size: value.size,
          quantity: value.quantity,
          color: value.color,
        },
      };
    case "collapse":
      return {
        ...state,
        isCollapsed: action.payload.bool,
      };
    default:
      return state;
  }
}

const ProductsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => {
    const read = async () => {
      const items = await readProducts("products");
      dispatch({ type: "setItems", payload: { items } });
    };

    const filterItems = (input) => {
      if (input === "") {
        dispatch({ type: "setItems", payload: { items: state.placeholders } });
        return;
      }
      const searchInput = input.toLowerCase();
      const results = state.placeholders.filter((item) =>
        item.title.toLowerCase().includes(searchInput) ||
        item.gender.toLowerCase().includes(searchInput) || 
        item.category.toLowerCase().includes(searchInput)
      );
      dispatch({ type: "filterItems", payload: { results } });
    };

    return {
      state,
      dispatch,
      read,
      filterItems,
    };
  }, [state]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useProductsContext = () => useContext(Context);

export default ProductsProvider;
